import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/core/auth/AuthContext';
import { flattenPermissionCatalog } from '../../catalog/permissionCatalog';
import { permissionPolicyService, type PermissionPolicy, type PermissionPolicyForm } from '../services/permissionPolicy.service';
import type { PermissionRole } from '../types/permissionsList.types';

export function usePermissionPolicies(roles: PermissionRole[]) {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const [form, setForm] = useState<PermissionPolicyForm>(() => permissionPolicyService.buildInitialForm(roles));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const policiesQuery = useQuery({
    queryKey: ['settings', 'permissions', 'policies', companyId],
    queryFn: () => permissionPolicyService.list(companyId),
  });

  const permissionOptions = useMemo(() => flattenPermissionCatalog().map((item) => ({
    key: item.key,
    label: `${item.module.name} / ${item.resource.name} / ${item.action.name}`,
  })), []);

  const resetForm = () => {
    setEditingId(null);
    setForm(permissionPolicyService.buildInitialForm(roles));
  };

  const editPolicy = (policy: PermissionPolicy) => {
    setEditingId(policy.id);
    setForm({
      id: policy.id,
      roleId: policy.roleId || roles[0]?.id || '',
      permissionKey: policy.permissionKey,
      scopeType: policy.scopeType,
      scopeValueText: Object.values(policy.scopeValues).flat().join(', '),
      effect: policy.effect,
      priority: policy.priority,
      isActive: policy.isActive,
      ownOnly: Boolean(policy.conditions.own_only),
      denySelfApproval: Boolean(policy.conditions.deny_self_approval),
      maxAmount: policy.conditions.max_amount ? String(policy.conditions.max_amount) : '',
    });
  };

  const savePolicy = async () => {
    if (!form.roleId || !form.permissionKey) {
      toast.error('Vui lòng chọn vai trò và quyền');
      return;
    }
    setIsSaving(true);
    try {
      await permissionPolicyService.save({ ...form, id: editingId || undefined }, companyId, profile?.id || null);
      await queryClient.invalidateQueries({ queryKey: ['settings', 'permissions', 'policies'] });
      toast.success('Đã lưu policy');
      resetForm();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Không thể lưu policy');
    } finally {
      setIsSaving(false);
    }
  };

  const removePolicy = async (policy: PermissionPolicy) => {
    setIsSaving(true);
    try {
      await permissionPolicyService.remove(policy, profile?.id || null);
      await queryClient.invalidateQueries({ queryKey: ['settings', 'permissions', 'policies'] });
      toast.success('Đã xóa policy');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Không thể xóa policy');
    } finally {
      setIsSaving(false);
    }
  };

  return {
    policies: policiesQuery.data || [],
    isLoading: policiesQuery.isLoading,
    isSaving,
    form,
    editingId,
    permissionOptions,
    setForm,
    resetForm,
    editPolicy,
    savePolicy,
    removePolicy,
  };
}
